import classnames from 'classnames';
import React, { useRef, useEffect, useState } from 'react';
import striptags from 'striptags';
import styles from './index.module.less';
import type { CardCtaProps, NavStripProps, CardPickerProps } from './types';
import { FontType, Type, Size } from '../../../core/enums';
import Button from '../../archived/button';
import { Icon } from '../../atoms/icon';
import { Typography } from '../../archived/typography';
import type { SupportedHtmlTags } from '../../../core/types';

const maxSelected = 3;

const CardCta = React.memo(
  ({
    card,
    removeCard,
    addCard,
    temporarilySelectedCards,
    customTags,
    categoryIndex,
  }: CardCtaProps) => {
    const {
      imgSrc,
      imgAlt,
      annualFee,
      cardName,
      shortName,
      ariaLabelAddCard,
      ariaLabelRemoveCard,
    } = card;
    const isSelected = temporarilySelectedCards.indexOf(shortName) > -1;
    const isDisabled =
      !isSelected && temporarilySelectedCards.length >= maxSelected;
    const onClick = isSelected ? removeCard : addCard;
    const ariaLabelRemove =
      ariaLabelRemoveCard || `Remove ${striptags(cardName as string)}`;
    const ariaLabelAdd =
      ariaLabelAddCard || `Add ${striptags(cardName as string)}`;
    const ariaLabel = isSelected ? ariaLabelRemove : ariaLabelAdd;
    const needHover = !isSelected && !isDisabled;
    const isShaded = categoryIndex % 2 !== 0;

    return (
      <button
        className={classnames(
          styles.cardContainer,
          {
            [styles.selected]: isSelected,
          },
          {
            [styles.needHover]: needHover,
          },
          {
            [styles.shaded]: isShaded,
          }
        )}
        onClick={() => onClick(shortName)}
        aria-label={ariaLabel}
        aria-pressed={isSelected || false}
        disabled={isDisabled}
        aria-disabled={isDisabled}
      >
        {isSelected && (
          <Icon
            directory="dls"
            imageName="dls-icon-cancel-circle"
            className={styles.isSelectedIcon}
          />
        )}
        <img src={imgSrc} alt={imgAlt} />
        <div className={styles.textContainer}>
          <Typography
            className={styles.cardTitle}
            Component={customTags?.cardCtaTitle as keyof SupportedHtmlTags}
            $family={FontType.HelveticaRegular}
          >
            {cardName}
          </Typography>
          <Typography
            className={styles.cardFee}
            Component={customTags?.cardCtaText as keyof SupportedHtmlTags}
            $family={FontType.HelveticaRegular}
          >
            {annualFee}
          </Typography>
        </div>
      </button>
    );
  }
);

const NavStrip = React.memo(
  ({
    backButtonText,
    onBackCtaClick,
    compareButtonText,
    onCompareCtaClick,
    topBackButtonReceiveFocus,
    customHtmlTags,
    title,
    qeId,
  }: NavStripProps) => {
    const backButtonRef = useRef<HTMLButtonElement>(null);
    const hasHeader = !!title;

    useEffect(() => {
      setTimeout(() => {
        if (hasHeader && topBackButtonReceiveFocus && backButtonRef.current) {
          backButtonRef.current.focus();
        }
      }, 200);
    }, [hasHeader, topBackButtonReceiveFocus]);

    return (
      <div
        className={classnames(styles.headerContainer, {
          [styles.isHeader]: hasHeader,
        })}
      >
        <div className={styles.buttonContainer}>
          <Button
            forwardedRef={backButtonRef}
            qeId={`${qeId}_card-picker-back-button`}
            onClick={onBackCtaClick}
            $type={Type.Tertiary}
            className={styles.backButton}
          >
            <Icon
              className={styles.leftIcon}
              directory="dls"
              imageName="dls-icon-left"
              $size={Size.SM}
            />
            {backButtonText}
          </Button>
          <Button
            qeId={`${qeId}_card-picker-compare-button`}
            onClick={onCompareCtaClick}
            $type={Type.Primary}
            className={styles.compareButton}
          >
            {compareButtonText}
          </Button>
        </div>
        {hasHeader && (
          <Typography
            Component={
              customHtmlTags?.cardPickerHeader as keyof SupportedHtmlTags
            }
            $family={FontType.BentonMedium}
            className={styles.title}
          >
            {title}
          </Typography>
        )}
      </div>
    );
  }
);

export const CardPicker = ({
  backButtonText,
  backButtonOnClick,
  compareButtonText,
  compareOnClick,
  title,
  categoriesOfCards,
  selectedCards,
  showSelectedCountInTitle = true,
  topBackButtonReceiveFocus,
  customHtmlTags,
  qeId,
}: CardPickerProps) => {
  const [temporarilySelectedCards, setTemporarilySelectedCards] = useState(
    selectedCards || []
  );

  const addCard = (shortName: string) => {
    if ((temporarilySelectedCards || []).length < maxSelected) {
      setTemporarilySelectedCards([...temporarilySelectedCards, shortName]);
    }
  };

  const removeCard = (shortName: string) => {
    setTemporarilySelectedCards(
      temporarilySelectedCards.filter((card) => card !== shortName)
    );
  };

  const titleVal = showSelectedCountInTitle
    ? `${title} (${(temporarilySelectedCards || []).length}/${maxSelected})`
    : title;

  const navStripProps = {
    onBackCtaClick: () => backButtonOnClick && backButtonOnClick(),
    onCompareCtaClick: () =>
      compareOnClick && compareOnClick(temporarilySelectedCards),
    backButtonText,
    compareButtonText,
    topBackButtonReceiveFocus,
    customHtmlTags,
    qeId,
  };

  return (
    <div className={styles.container}>
      <NavStrip {...navStripProps} title={titleVal} />
      <div className={styles.categoriesContainer}>
        {(categoriesOfCards || []).map((category, cateIndex) => {
          const { title, cards } = category;
          return (
            <div className={styles.categoryContainer} key={cateIndex}>
              <Typography
                className={styles.categoryTitle}
                Component={
                  customHtmlTags?.cardCategoryTitle as keyof SupportedHtmlTags
                }
              >
                {title}
              </Typography>
              <div className={styles.cardsContainer}>
                {(cards || []).map((card, i) => (
                  <CardCta
                    key={i}
                    card={card}
                    addCard={addCard}
                    removeCard={removeCard}
                    temporarilySelectedCards={temporarilySelectedCards}
                    customTags={customHtmlTags}
                    categoryIndex={cateIndex}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <NavStrip {...navStripProps} />
    </div>
  );
};

CardPicker.defaultProps = {
  backButtonText: 'Back',
  backButtonOnClick: () => {},
  compareButtonText: 'Compare Selected Cards',
  compareOnClick: () => {},
  title: 'Select Up to 3 Cards',
  categoriesOfCards: [],
  selectedCards: [],
  showSelectedCountInTitle: true,
  topBackButtonReceiveFocus: true,
  customHtmlTags: {
    cardCtaTitle: 'h3',
    cardCtaText: 'p',
    cardPickerHeader: 'h1',
    cardCategoryTitle: 'h2',
  },
  qeId: 'CompareCardPicker',
};

export default CardPicker;
